import React from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Icon, Text } from "@performyard/py-components";

import type { CycleType } from "./Config";
import { CycleTypeCard } from "./components/CycleTypeCard";

import { useAvailableCycleTypes } from "./hooks/useAvailableCycleTypes";

type ChooseCycleTypeProps = {
	selectedType: CycleType | null;
	onSelect: (type: CycleType) => void;
	onCancel: () => void;
	onGetStarted: () => void;
};

export const ChooseCycleType = ({ selectedType, onSelect, onCancel, onGetStarted }: ChooseCycleTypeProps) => {
	const { options, isLoading } = useAvailableCycleTypes();
	if (isLoading) return null;

	return (
		<div className="absolute inset-0 flex flex-col items-center gap-8 bg-white px-6 pt-6 pb-10">
			<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="w-full text-center">
				Select A Review Cycle Type
			</Text>

			<div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-auto bg-neutral-1">
				<fieldset className="flex flex-wrap justify-center gap-10 border-none p-6">
					<legend className="sr-only">Review cycle type</legend>
					{options.map((option) => (
						<label key={option.type} htmlFor={`cycle-type-${option.type}`}>
							<input
								type="radio"
								id={`cycle-type-${option.type}`}
								name="cycle-type"
								value={option.type}
								checked={selectedType === option.type}
								disabled={option.disabled}
								onChange={() => onSelect(option.type)}
								className="peer sr-only"
							/>
							<CycleTypeCard
								option={option}
								selected={selectedType === option.type}
								disabled={option.disabled}
							/>
							{option?.disabled && option.type === "group" && (
								<div className="mt-2 flex items-center gap-1">
									<Icon icon={faCircleInfo} size={12} color="neutral-7" aria-hidden="true" />
									<Text size="paragraph-3" weight="regular">
										Create a group in PerformYard to enable this cycle type.
									</Text>
								</div>
							)}
						</label>
					))}
				</fieldset>
			</div>

			<div className="flex shrink-0 items-center justify-center gap-2">
				<ButtonStandard variant="outline" color="neutral" size="large" onClick={onCancel}>
					Cancel
				</ButtonStandard>
				<ButtonStandard
					variant="solid"
					color="primary"
					size="large"
					disabled={!selectedType}
					onClick={onGetStarted}
				>
					Get Started
				</ButtonStandard>
			</div>
		</div>
	);
};
